/**
 * Dev-only helpers to measure work before micro-optimizing (filter apply, merges, etc.).
 *
 * Enable console logs:
 *   `localStorage.setItem('perf:log', '1'); location.reload()`
 * Wrap expensive pure work in `measureSync('label', () => …)` — logs only when `> 2ms`.
 * Use `<DevProfiler id="…">` (see dev-profiler.tsx) to log React commits with `actualDuration > 8ms`.
 */

const shouldLog =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  window.localStorage?.getItem("perf:log") === "1";

export function measureSync<T>(label: string, fn: () => T): T {
  if (!shouldLog) {
    return fn();
  }
  const start = performance.now();
  try {
    return fn();
  } finally {
    const ms = performance.now() - start;
    if (ms > 2) {
      // eslint-disable-next-line no-console
      console.debug(`[perf] ${label}: ${ms.toFixed(2)}ms`);
    }
  }
}

/** React Profiler onRender callback: log slow commits when perf:log is on. */
export function logProfilerCommit(
  id: string,
  _phase: "mount" | "update" | "nested-update",
  actualDuration: number,
): void {
  if (!shouldLog) {
    return;
  }
  if (actualDuration > 8) {
    // eslint-disable-next-line no-console
    console.debug(`[perf] <Profiler id="${id}"> actualDuration=${actualDuration.toFixed(2)}ms`);
  }
}
